import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { client } from "../../Clients";
import type { StorageFolder, StorageObject } from "../../API";
import { formatExactDate, formatRelativeTime } from "../../Helper/dates";
import type { IApplicationState } from "../../Redux/Reducers";

import type { UploadKind, UploadFormState, Step, ViewMode, BrowseState, BrowseItem } from "./types";
import {
	ROOT_PLACEHOLDER,
	DEFAULT_STORAGE_PATH,
	ROOT_PATHS,
	BROWSE_PAGE_SIZE,
	URL_PATH_PARAM,
	initialFormState,
	PUBLICATION_EXTENSIONS
} from "./types";
import {
	normalizePath,
	createBrowseStateFromResponse,
	isSystemKind,
	buildPublicUrl,
	resolveCompressedKey,
	resolveOriginalKey,
	resolveDisplayKey,
	formatFileSize,
	extractErrorMessage,
	resolvePathValue
} from "./utils";
import { IconFolder, IconFile, IconImageStack, IconDocumentStack } from "./StorageIcons";
import { FilePreviewModal } from "./FilePreviewModal";
import { UploadModal } from "./UploadModal";

/** File manager with image and publication uploads, folder navigation and taxonomies. */
const Storage: React.FC = () => {
	const [selection, setSelection] = useState<UploadKind>("image");
	const [modalOpen, setModalOpen] = useState(false);
	const [form, setForm] = useState<UploadFormState>(initialFormState);
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [successMessage, setSuccessMessage] = useState<string | null>(null);
	const [currentStep, setCurrentStep] = useState<Step>(1);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const [previewType, setPreviewType] = useState<"image" | "pdf" | "other" | null>(null);
	const location = useLocation();
	const history = useHistory();
	const [currentPath, setCurrentPath] = useState<string>(() => {
		const params = new URLSearchParams(location.search);
		return resolvePathValue(params.get(URL_PATH_PARAM));
	});
	const [browsePage, setBrowsePage] = useState(1);
	const [browseState, setBrowseState] = useState<BrowseState>(() => createBrowseStateFromResponse());
	const [browseLoading, setBrowseLoading] = useState(false);
	const [browseError, setBrowseError] = useState<string | null>(null);
	const [refreshToken, setRefreshToken] = useState(0);
	const [searchInput, setSearchInput] = useState("");
	const [search, setSearch] = useState("");
	const [viewMode, setViewMode] = useState<ViewMode>("grid");
	const [previewObject, setPreviewObject] = useState<StorageObject | null>(null);
	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewTaxonomyIds, setPreviewTaxonomyIds] = useState<string[]>([]);
	const [previewTaxonomySaving, setPreviewTaxonomySaving] = useState(false);
	const [previewTaxonomyMessage, setPreviewTaxonomyMessage] = useState<string | null>(null);
	const [previewTaxonomyEditing, setPreviewTaxonomyEditing] = useState(false);
	const [recompressing, setRecompressing] = useState(false);
	const taxonomies = useSelector((state: IApplicationState) => state.taxonomies.items);

	const steps = useMemo(
		() => [
			{ id: 1 as Step, label: "Destination" },
			{ id: 2 as Step, label: "Upload file" },
			{ id: 3 as Step, label: "Details" }
		],
		[]
	);

	const taxonomyMap = useMemo(() => {
		const map = new Map<string, string>();
		taxonomies.forEach((taxonomie) => {
			if (taxonomie.id) {
				map.set(taxonomie.id, taxonomie.title ?? taxonomie.id);
			}
		});
		return map;
	}, [taxonomies]);

	const navigateToPath = useCallback(
		(value: string) => {
			const normalized = value === ROOT_PLACEHOLDER ? ROOT_PLACEHOLDER : resolvePathValue(value);
			setCurrentPath(normalized);
			const params = new URLSearchParams(location.search);
			if (normalized === ROOT_PLACEHOLDER) {
				params.delete(URL_PATH_PARAM);
			} else {
				params.set(URL_PATH_PARAM, normalized);
			}
			const nextSearch = params.toString();
			history.replace({
				pathname: location.pathname,
				search: nextSearch ? `?${nextSearch}` : ""
			});
		},
		[history, location.pathname, location.search]
	);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const normalized = resolvePathValue(params.get(URL_PATH_PARAM));
		setCurrentPath((prev) => (prev === normalized ? prev : normalized));
	}, [location.search]);

	useEffect(() => {
		setBrowsePage(1);
		setSearchInput("");
		setSearch("");
	}, [currentPath]);

	// Debounce the search field so typing doesn't fire a request per keystroke.
	useEffect(() => {
		const handle = window.setTimeout(() => {
			setSearch(searchInput.trim());
			setBrowsePage(1);
		}, 300);
		return () => window.clearTimeout(handle);
	}, [searchInput]);

	useEffect(() => {
		let cancelled = false;
		const fetchBrowse = async () => {
			if (currentPath === ROOT_PLACEHOLDER) {
				setBrowseError(null);
				setBrowseState(createBrowseStateFromResponse());
				setBrowseLoading(false);
				return;
			}
			setBrowseLoading(true);
			setBrowseError(null);
			try {
				const response = await client.storage.apiStorageBrowseGet(
					currentPath,
					browsePage,
					BROWSE_PAGE_SIZE,
					search || undefined
				);
				if (cancelled) return;
				setBrowseState(createBrowseStateFromResponse(response?.data));
			} catch (error) {
				if (cancelled) return;
				console.error("Failed to browse storage", error);
				setBrowseState(createBrowseStateFromResponse());
				const message = extractErrorMessage(error);
				setBrowseError(message ?? "Unable to browse the requested folder.");
			} finally {
				if (!cancelled) {
					setBrowseLoading(false);
				}
			}
		};
		fetchBrowse();
		return () => {
			cancelled = true;
		};
	}, [currentPath, refreshToken, browsePage, search]);

	const handleResetPath = () => {
		navigateToPath(DEFAULT_STORAGE_PATH);
	};

	const handleRefresh = () => {
		setRefreshToken((token) => token + 1);
	};

	const handlePrevPage = () => {
		setBrowsePage((current) => Math.max(1, current - 1));
	};

	const handleNextPage = () => {
		if (!hasMore) return;
		setBrowsePage((current) => current + 1);
	};

	const handleSavePreviewTaxonomies = async () => {
		if (!previewObject?.id) return;
		setPreviewTaxonomySaving(true);
		setPreviewTaxonomyMessage(null);
		try {
			const response = await client.storage.apiStorageIdTaxonomiesPut(previewObject.id, {
				taxonomyIds: previewTaxonomyIds
			});
			const updated = response.data ?? { ...previewObject, taxonomyIds: previewTaxonomyIds };
			setPreviewObject(updated);
			setPreviewTaxonomyMessage("Taxonomies updated.");
			setPreviewTaxonomyEditing(false);
		} catch (error) {
			console.error(error);
			setPreviewTaxonomyMessage("Unable to update taxonomies.");
		} finally {
			setPreviewTaxonomySaving(false);
		}
	};

	const handleRecompress = async () => {
		if (!previewObject?.id) return;
		setRecompressing(true);
		try {
			const response = await client.storage.apiStorageIdRecompressPost(previewObject.id);
			const updated = response.data ?? previewObject;
			setPreviewObject(updated);
			setPreviewTaxonomyMessage("Image recompressed.");
			handleRefresh();
		} catch (error) {
			console.error(error);
			const message = extractErrorMessage(error);
			setPreviewTaxonomyMessage(message ?? "Unable to recompress image.");
		} finally {
			setRecompressing(false);
		}
	};

	const handlePreviewOpen = useCallback((object: StorageObject) => {
		setPreviewObject(object);
		setPreviewTaxonomyIds((object.taxonomyIds ?? []).filter((id): id is string => Boolean(id && id.trim())).map((id) => id.trim()));
		setPreviewTaxonomyMessage(null);
		setPreviewTaxonomyEditing(false);
		setPreviewOpen(true);
	}, []);

	const handlePreviewClose = () => {
		setPreviewOpen(false);
		setPreviewObject(null);
		setPreviewTaxonomyIds([]);
		setPreviewTaxonomyMessage(null);
		setPreviewTaxonomyEditing(false);
	};

	const previewDisplayKey = useMemo(() => resolveDisplayKey(previewObject), [previewObject]);
	const previewOriginalKey = useMemo(() => resolveOriginalKey(previewObject), [previewObject]);
	const previewCompressedKey = useMemo(() => resolveCompressedKey(previewObject), [previewObject]);
	const previewAssetUrl = useMemo(() => (previewDisplayKey ? buildPublicUrl(previewDisplayKey) : null), [previewDisplayKey]);
	const previewOriginalUrl = useMemo(() => (previewOriginalKey ? buildPublicUrl(previewOriginalKey) : null), [previewOriginalKey]);
	const previewCompressedUrl = useMemo(
		() => (previewCompressedKey ? buildPublicUrl(previewCompressedKey) : null),
		[previewCompressedKey]
	);

	const { folder, breadcrumbs, children, objects, page, hasMore, totalObjects } = browseState;
	const totalPages = totalObjects > 0 ? Math.max(1, Math.ceil(totalObjects / BROWSE_PAGE_SIZE)) : 1;
	const isRootView = currentPath === ROOT_PLACEHOLDER;

	const browseItems = useMemo<BrowseItem[]>(() => {
		const folderItems: BrowseItem[] = children
			.filter((child): child is StorageFolder => Boolean(child && child.path))
			.map((child, index) => ({
				type: "folder",
				key: child.id ?? child.path ?? `folder-${index}`,
				name: child.name ?? child.path ?? "Untitled folder",
				path: child.path ?? "",
				folder: child
			}));
		const fileItems: BrowseItem[] = objects
			.filter((object): object is StorageObject => Boolean(object))
			.map((object, index) => ({
				type: "file",
				key: object.id ?? object.key ?? `${object.folder ?? "folder"}-${object.fileName ?? index}`,
				name: object.title ?? object.fileName ?? "Untitled asset",
				path: object.key ?? object.folder ?? "",
				object
			}));
		return [...folderItems, ...fileItems];
	}, [children, objects]);

	const rootBrowseItems = useMemo<BrowseItem[]>(() => {
		if (!isRootView) {
			return [];
		}
		return ROOT_PATHS.map((root) => ({
			type: "folder",
			key: `root-${root.path}`,
			name: root.label,
			path: root.path,
			folder: {
				id: root.path,
				name: root.label,
				path: root.path,
				parentPath: null,
				kind: root.kind,
				depth: 0
			} as StorageFolder
		}));
	}, [isRootView]);

	const displayedItems = isRootView ? rootBrowseItems : browseItems;
	const totalChildren = isRootView ? rootBrowseItems.length : children.length;
	const totalAssetCount = isRootView ? 0 : totalObjects;
	const folderStatsLabel = isRootView ? "Root" : folder?.path ?? currentPath;
	const folderKindLabel = isRootView ? "All folders" : folder?.kind ?? "Unknown";

	const getFolderVisuals = (path: string, kind?: string | null) => {
		const rootMeta = ROOT_PATHS.find((root) => root.path === path);
		if (rootMeta) {
			const accentClass = rootMeta.icon === "image" ? "bg-amber-100 text-amber-600" : "bg-sky-100 text-sky-600";
			const IconComponent = rootMeta.icon === "image" ? IconImageStack : IconDocumentStack;
			return {
				accentClass,
				iconElement: <IconComponent className="h-10 w-10" />
			};
		}

		if ((kind ?? "").toLowerCase().includes("publication")) {
			return { accentClass: "bg-sky-100 text-sky-600", iconElement: <IconDocumentStack className="h-10 w-10" /> };
		}

		return { accentClass: "bg-slate-100 text-slate-500", iconElement: <IconFolder className="h-10 w-12" /> };
	};

	const getFileVisuals = (object: StorageObject) => {
		const type = object.contentType ?? "";
		if (type.startsWith("image/")) {
			return { accentClass: "bg-amber-50 text-amber-600" };
		}
		if (type.includes("pdf") || type.includes("msword") || type.includes("presentation") || type.includes("powerpoint")) {
			return { accentClass: "bg-sky-50 text-sky-600" };
		}
		return { accentClass: "bg-slate-100 text-slate-500" };
	};

	useEffect(() => {
		return () => {
			if (previewUrl) {
				URL.revokeObjectURL(previewUrl);
			}
		};
	}, [previewUrl]);

	const openModal = () => {
		setSubmitError(null);
		setSuccessMessage(null);
		setForm(initialFormState);
		setCurrentStep(1);
		if (previewUrl) {
			URL.revokeObjectURL(previewUrl);
		}
		setPreviewUrl(null);
		setPreviewType(null);
		setModalOpen(true);
	};

	const closeModal = () => {
		if (submitting) return;
		setModalOpen(false);
	};

	const handleInputChange = (key: keyof UploadFormState, value: string | string[] | File | null) => {
		setForm((prev) => ({
			...prev,
			[key]: value as never
		}));
	};

	const handleTitleChange = (value: string) => {
		handleInputChange("title", value);
		if (!form.fileName) {
			const slug = value
				.toLowerCase()
				.trim()
				.replace(/\s+/g, "-")
				.replace(/[^a-z0-9-_]/g, "");
			handleInputChange("fileName", slug);
		}
	};

	const clearFileSelection = useCallback(() => {
		setForm((prev) => ({ ...prev, file: null }));
		setPreviewType(null);
		setPreviewUrl((prevUrl) => {
			if (prevUrl) {
				URL.revokeObjectURL(prevUrl);
			}
			return null;
		});
	}, []);

	const handleFileChange = (file: File | null) => {
		handleInputChange("file", file);
		setSuccessMessage(null);
		setPreviewUrl((prevUrl) => {
			if (prevUrl) {
				URL.revokeObjectURL(prevUrl);
			}
			return null;
		});
		setPreviewType(null);

		if (file && !form.fileName) {
			const base = file.name.substring(0, file.name.lastIndexOf(".")) || file.name;
			handleInputChange("fileName", base);
		}

		if (file) {
			const url = URL.createObjectURL(file);
			setPreviewUrl(url);
			if (file.type.startsWith("image/")) {
				setPreviewType("image");
			} else if (file.type === "application/pdf") {
				setPreviewType("pdf");
			} else {
				setPreviewType("other");
			}
		}
	};

	useEffect(() => {
		if (!form.file) return;

		const fileType = form.file.type;
		const isImage = fileType.startsWith("image/");
		const extension = form.file.name.split(".").pop()?.toLowerCase() ?? "";
		const isPublicationAllowed = PUBLICATION_EXTENSIONS.has(extension);

		if (selection === "image" && !isImage) {
			clearFileSelection();
		} else if (selection === "publication" && !(fileType === "application/pdf" || isPublicationAllowed)) {
			clearFileSelection();
		}
	}, [selection, form.file, clearFileSelection]);

	const canSubmit = form.title.trim().length > 0 && form.file !== null;
	const canAdvance = currentStep === 1 ? Boolean(selection) : currentStep === 2 ? form.file !== null : false;

	const handleNext = () => {
		if (!canAdvance) return;
		setCurrentStep((prev) => (prev === 1 ? 2 : 3));
	};

	const handleBack = () => {
		setCurrentStep((prev) => (prev === 3 ? 2 : 1));
	};

	const handleSubmit = async () => {
		if (!canSubmit || !form.file) {
			return;
		}
		setSubmitting(true);
		setSubmitError(null);
		setSuccessMessage(null);
		try {
			const title = form.title.trim();
			const fileName = form.fileName.trim();
			const folder = form.folder.trim();
			const taxonomyFilter = form.taxonomyIds.length ? form.taxonomyIds.join(",") : undefined;

			if (selection === "image") {
				await client.storage
					.apiStorageUploadImagesPost(
						title,
						form.file,
						fileName.length ? fileName : undefined,
						folder.length ? folder : undefined,
						taxonomyFilter
					)
					.then((response) => response.data);
			} else {
				await client.storage
					.apiStorageUploadPublicationsPost(
						title,
						form.file,
						fileName.length ? fileName : undefined,
						folder.length ? folder : undefined,
						taxonomyFilter
					)
					.then((response) => response.data);
			}

			setSuccessMessage("Asset uploaded successfully.");
			setForm(initialFormState);
		} catch (error) {
			console.error(error);
			const message = extractErrorMessage(error);
			setSubmitError(message ? `Upload failed: ${message}` : "Upload failed. Please verify the form and try again.");
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div className="min-h-full p-6">
			<div className="mx-auto w-full space-y-8">
				<div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
					<div>
						<p className="text-xs uppercase tracking-widest text-slate-500">Storage</p>
						<h1 className="text-2xl font-semibold text-slate-900">Manage storage</h1>
						<p className="text-sm text-slate-500">Upload images and publication files into Cloudflare R2.</p>
					</div>
					<button
						type="button"
						onClick={openModal}
						className="ml-auto inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800"
					>
						Upload asset
					</button>
				</div>
				<section className="space-y-6">
					<div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
						<div className="flex flex-wrap items-center justify-between gap-3">
							<div>
								<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Location</p>
								<div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
									<button
										type="button"
										onClick={() => navigateToPath(ROOT_PLACEHOLDER)}
										disabled={isRootView}
										className={`rounded-full px-3 py-1 text-xs font-semibold ${
											isRootView ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
										} disabled:cursor-default`}
									>
										Root
									</button>
									{breadcrumbs.length ? (
										breadcrumbs.map((crumb, index) => {
											const crumbPath = crumb.path ?? "";
											const isLast = index === breadcrumbs.length - 1;
											return (
												<React.Fragment key={crumb.id ?? crumb.path ?? `${crumb.name}-${index}`}>
													<span className="text-slate-400">/</span>
													<button
														type="button"
														onClick={() => crumbPath && navigateToPath(crumbPath)}
														disabled={isLast || !crumbPath}
														className={`rounded-full px-3 py-1 text-xs font-semibold ${
															isLast
																? "bg-slate-900 text-white"
																: crumbPath
																	? "bg-slate-100 text-slate-700 hover:bg-slate-200"
																	: "bg-slate-100 text-slate-400"
														} disabled:cursor-default`}
													>
														{crumb.name ?? crumb.path ?? "Folder"}
													</button>
												</React.Fragment>
											);
										})
									) : (
										<span className="text-xs text-slate-500">No breadcrumbs yet.</span>
									)}
								</div>
							</div>
							<div className="flex items-center gap-2">
								<button
									type="button"
									onClick={handleRefresh}
									disabled={browseLoading || isRootView}
									className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
								>
									Refresh
								</button>
							</div>
						</div>
						<div className="mt-6 grid gap-3 text-sm text-slate-600 sm:grid-cols-2 xl:grid-cols-4">
								<div className="rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2">
									<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Folder</p>
									<p className="mt-1 break-all text-sm font-semibold text-slate-900">{folderStatsLabel}</p>
								</div>
								<div className="rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2">
									<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Kind</p>
									<p className="mt-1 text-sm font-semibold text-slate-900">{folderKindLabel}</p>
								</div>
								<div className="rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2">
									<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Children</p>
									<p className="mt-1 text-2xl font-semibold text-slate-900">{totalChildren}</p>
								</div>
								<div className="rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2">
									<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
										Assets{search ? " (filtered)" : ""}
									</p>
									<p className="mt-1 text-2xl font-semibold text-slate-900">{totalAssetCount}</p>
								</div>
								<div className="rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2 sm:col-span-2 xl:col-span-4">
									<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Updated</p>
									<p className="mt-1 text-sm font-semibold text-slate-900">
										{!isRootView && folder?.updatedAt && !isSystemKind(folder?.kind) ? (
											<>
												{formatRelativeTime(folder.updatedAt)}
												<span className="ml-1 text-xs font-normal text-slate-500">({formatExactDate(folder.updatedAt)})</span>
											</>
										) : (
											"\u2014"
										)}
									</p>
								</div>
							</div>
						{browseLoading && (
							<p className="mt-4 text-xs text-slate-500">{isRootView ? "Select a folder to start browsing." : "Loading folder\u2026"}</p>
						)}
						{browseError && (
							<p className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
								{browseError}
								<button
									type="button"
									onClick={handleResetPath}
									className="ml-3 font-semibold underline decoration-rose-400 underline-offset-2"
								>
									Go to root
								</button>
							</p>
						)}
					</div>
					<div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
						<div className="mb-4 flex flex-wrap items-center gap-3">
							<div>
								<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Items</p>
							</div>
							{!isRootView && (
								<div className="flex flex-1 items-center sm:max-w-sm">
									<input
										type="search"
										value={searchInput}
										onChange={(e) => setSearchInput(e.target.value)}
										placeholder="Search files in this folder…"
										className="w-full rounded-full border border-slate-200 bg-slate-50 px-4 py-1.5 text-sm text-slate-700 placeholder:text-slate-400 focus:border-slate-400 focus:bg-white focus:outline-none"
									/>
								</div>
							)}
							<div className="ml-auto flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 p-1">
								<button
									type="button"
									onClick={() => setViewMode("grid")}
									className={`rounded-full px-3 py-1 text-xs font-semibold transition ${viewMode === "grid" ? "bg-white text-slate-900 shadow" : "text-slate-500 hover:text-slate-700"}`}
								>
									Grid
								</button>
								<button
									type="button"
									onClick={() => setViewMode("list")}
									className={`rounded-full px-3 py-1 text-xs font-semibold transition ${viewMode === "list" ? "bg-white text-slate-900 shadow" : "text-slate-500 hover:text-slate-700"}`}
								>
									List
								</button>
							</div>
						</div>
						{!isRootView && (
							<div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-2 text-xs font-semibold text-slate-600">
								<div>
									Page {page} of {totalPages}
									<span className="ml-2 font-normal text-slate-500">
										({totalAssetCount} {totalAssetCount === 1 ? "asset" : "assets"}
										{search ? ` matching "${search}"` : ""})
									</span>
								</div>
								<div className="flex items-center gap-2">
									<button
										type="button"
										onClick={handlePrevPage}
										disabled={page === 1 || browseLoading}
										className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
									>
										Previous
									</button>
									<button
										type="button"
										onClick={handleNextPage}
										disabled={!hasMore || browseLoading}
										className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
									>
										Next
									</button>
								</div>
							</div>
						)}
						{browseLoading && !displayedItems.length ? (
							<p className="text-sm text-slate-500">Loading items\u2026</p>
						) : displayedItems.length ? (
							<div className="space-y-3">
								{browseLoading && <p className="text-xs text-slate-500">Refreshing\u2026</p>}
								{viewMode === "grid" ? (
									<div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-7">
										{displayedItems.map((item) => {
											if (item.type === "folder") {
												const { accentClass, iconElement } = getFolderVisuals(item.path, item.folder.kind);
												return (
													<button
														type="button"
														key={item.key}
														onClick={() => item.path && navigateToPath(item.path)}
														className="flex h-full flex-col items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-center shadow-sm transition hover:border-slate-300 hover:shadow-lg"
													>
														<div className={`flex h-16 w-16 items-center justify-center rounded-2xl ${accentClass}`}>{iconElement}</div>
														<div>
															<p className="text-sm font-semibold text-slate-900">{item.name}</p>
															<p className="mt-1 text-xs text-slate-500">{item.path || "\u2014"}</p>
														</div>
													</button>
												);
											}

											const publicUrl = buildPublicUrl(resolveDisplayKey(item.object));
											const { accentClass } = getFileVisuals(item.object);
											return (
												<button
													type="button"
													key={item.key}
													onClick={() => handlePreviewOpen(item.object)}
													className="flex h-full flex-col items-center gap-2 rounded-2xl border border-slate-200 bg-white/90 p-4 text-center shadow-sm transition hover:border-slate-300 hover:shadow-lg"
												>
													<div className={`flex h-16 w-16 items-center justify-center rounded-2xl ${accentClass}`}>
														<IconFile className="h-7 w-7" />
													</div>
													<div>
														<p className="truncate text-sm font-semibold text-slate-900">{item.name}</p>
														<p className="mt-1 text-xs text-slate-500">{formatFileSize(item.object.sizeBytes)}</p>
													</div>
													{!publicUrl && <p className="text-xs text-slate-400">No URL</p>}
												</button>
											);
										})}
									</div>
								) : (
									<div className="overflow-x-auto">
										<table className="min-w-full divide-y divide-slate-200 text-sm">
											<thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
												<tr>
													<th scope="col" className="px-4 py-3 text-left">
														Type
													</th>
													<th scope="col" className="px-4 py-3 text-left">
														Name
													</th>
													<th scope="col" className="px-4 py-3 text-left">
														Path / Location
													</th>
													<th scope="col" className="px-4 py-3 text-left">
														Size
													</th>
													<th scope="col" className="px-4 py-3 text-left">
														Updated
													</th>
													<th scope="col" className="px-4 py-3 text-left">
														Action
													</th>
												</tr>
											</thead>
											<tbody className="divide-y divide-slate-100">
												{displayedItems.map((item) => {
													if (item.type === "folder") {
														const folderListIcon = (() => {
															const { iconElement } = getFolderVisuals(item.path, item.folder.kind);
															return React.cloneElement(iconElement, { className: "h-5 w-5" });
														})();
														return (
															<tr key={item.key} className="align-top">
																<td className="whitespace-nowrap px-4 py-3 text-slate-600">
																	<div className="flex items-center gap-2">
																		<span className="text-slate-500">{folderListIcon}</span>
																		<span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Folder</span>
																	</div>
																</td>
																<td className="px-4 py-3">
																	<button type="button" onClick={() => item.path && navigateToPath(item.path)} className="font-semibold text-slate-900 hover:underline">
																		{item.name}
																	</button>
																	<p className="text-xs text-slate-500">Depth {item.folder.depth ?? 0}</p>
																</td>
																<td className="px-4 py-3 text-xs text-slate-600">{item.path || "\u2014"}</td>
																<td className="px-4 py-3 text-slate-500">\u2014</td>
																<td className="px-4 py-3 text-sm text-slate-700">
																	{item.folder.updatedAt && !isSystemKind(item.folder.kind) ? (
																		<>
																			{formatRelativeTime(item.folder.updatedAt)}
																			<p className="text-xs text-slate-400">{formatExactDate(item.folder.updatedAt)}</p>
																		</>
																	) : (
																		"\u2014"
																	)}
																</td>
																<td className="px-4 py-3">
																	<button
																		type="button"
																		onClick={() => item.path && navigateToPath(item.path)}
																		className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
																	>
																		Open folder
																	</button>
																</td>
															</tr>
														);
													}

													const publicUrl = buildPublicUrl(resolveDisplayKey(item.object));
													const { accentClass } = getFileVisuals(item.object);
													return (
														<tr key={item.key} className="align-top">
															<td className="whitespace-nowrap px-4 py-3 text-slate-600">
																<div className="flex items-center gap-2">
																	<div className={`flex h-8 w-8 items-center justify-center rounded-full text-xs ${accentClass}`}>
																		<IconFile className="h-4 w-4" />
																	</div>
																	<span className="text-xs font-semibold uppercase tracking-wide text-slate-500">File</span>
																</div>
															</td>
															<td className="px-4 py-3">
																<p className="font-semibold text-slate-900">{item.name}</p>
																<p className="text-xs text-slate-500">{item.object.fileName ?? "\u2014"}</p>
															</td>
															<td className="px-4 py-3 text-xs text-slate-600">{item.path || item.object.folder || "\u2014"}</td>
															<td className="px-4 py-3 text-slate-700">{formatFileSize(item.object.sizeBytes)}</td>
															<td className="px-4 py-3 text-sm text-slate-700">
																{formatRelativeTime(item.object.updatedAt)}
																<p className="text-xs text-slate-400">{formatExactDate(item.object.updatedAt)}</p>
															</td>
															<td className="px-4 py-3">
																<button
																	type="button"
																	onClick={() => handlePreviewOpen(item.object)}
																	className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
																>
																	Preview
																</button>
															</td>
														</tr>
													);
												})}
											</tbody>
										</table>
									</div>
								)}
								{!isRootView && (hasMore || page > 1) && (
									<div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
										<button
											type="button"
											onClick={handlePrevPage}
											disabled={page === 1 || browseLoading}
											className="rounded-full border border-slate-300 px-4 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
										>
											Previous
										</button>
										<div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
											Page {page} of {totalPages}
										</div>
										<button
											type="button"
											onClick={handleNextPage}
											disabled={!hasMore || browseLoading}
											className="rounded-full border border-slate-300 px-4 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
										>
											Next
										</button>
									</div>
								)}
							</div>
						) : (
							<p className="text-sm text-slate-500">
								{isRootView ? "Pick a folder to start browsing." : "No items inside this path yet."}
							</p>
						)}
					</div>
				</section>
			</div>

			<UploadModal
				modalOpen={modalOpen}
				submitting={submitting}
				currentStep={currentStep}
				selection={selection}
				form={form}
				previewUrl={previewUrl}
				previewType={previewType}
				canAdvance={canAdvance}
				canSubmit={canSubmit}
				submitError={submitError}
				successMessage={successMessage}
				taxonomies={taxonomies}
				steps={steps}
				onClose={closeModal}
				onNext={handleNext}
				onBack={handleBack}
				onSubmit={handleSubmit}
				onSelectionChange={setSelection}
				onFileChange={handleFileChange}
				onTitleChange={handleTitleChange}
				onInputChange={handleInputChange}
			/>

			<FilePreviewModal
				previewOpen={previewOpen}
				previewObject={previewObject}
				previewAssetUrl={previewAssetUrl}
				previewOriginalUrl={previewOriginalUrl}
				previewCompressedUrl={previewCompressedUrl}
				previewTaxonomyIds={previewTaxonomyIds}
				previewTaxonomySaving={previewTaxonomySaving}
				previewTaxonomyMessage={previewTaxonomyMessage}
				previewTaxonomyEditing={previewTaxonomyEditing}
				recompressing={recompressing}
				taxonomies={taxonomies}
				taxonomyMap={taxonomyMap}
				onClose={handlePreviewClose}
				onTaxonomyEditToggle={() => setPreviewTaxonomyEditing(true)}
				onTaxonomyIdsChange={setPreviewTaxonomyIds}
				onSaveTaxonomies={handleSavePreviewTaxonomies}
				onRecompress={handleRecompress}
			/>
		</div>
	);
};

export default Storage;
